import { useState, useCallback, useEffect } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { canFiscalRelay, fiscalForward } from '../utils/printService';

// Admin/Kassir üçün ortaq Fiskal Kassa əməliyyat modalı (Caspos + SmartOne + TapXPhone).
// Relay (C# host) varsa prepare→fiscalForward→complete; yoxdursa birbaşa /cashbox/{action}.
export default function FiskalKassaModal({ open, onClose }) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState('');            // hazırda gedən əməliyyat açarı
    const [lastResult, setLastResult] = useState(null); // {action, label, data, raw} — cihaz cavabı
    const today = new Date().toISOString().slice(0, 10);
    const [periodFrom, setPeriodFrom] = useState(today);
    const [periodTo, setPeriodTo] = useState(today);
    const [checkNum, setCheckNum] = useState('');

    const loadStatus = useCallback(async () => {
        try {
            const res = await api.get('/cashbox/status');
            setStatus(res.data?.data || null);
        } catch { setStatus(null); }
    }, []);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        loadStatus().finally(() => setLoading(false));
    }, [open, loadStatus]);

    // Əməliyyatı icra et: relay (C#) varsa ondan, yoxsa birbaşa server→cihaz.
    // Cihaz cavabını (result.data / raw) ekranda göstərir — "heçnə olmur" təəssüratı qalmasın.
    const run = async (action, successMsg, payload = {}, label = '') => {
        setBusy(action);
        try {
            let msg = successMsg; let result = null;
            if (canFiscalRelay()) {
                const prep = await api.post('/cashbox/prepare', { action, payload });
                const fwd = await fiscalForward(prep.data.relay);
                const done = await api.post('/cashbox/complete', { action, raw: fwd.raw, status: fwd.status });
                msg = done.data?.msg || successMsg; result = done.data?.data?.result || null;
            } else {
                const r = await api.post(`/cashbox/${action}`, { payload });
                msg = r.data?.msg || successMsg; result = r.data?.data?.result || null;
            }
            toast.success(msg);
            setLastResult({ action, label: label || msg, data: result?.data ?? null, raw: result?.raw ?? null });
            await loadStatus();
        } catch (err) {
            toast.error(err.response?.data?.msg || err.message || 'Xəta baş verdi.');
        } finally {
            setBusy('');
        }
    };

    if (!open) return null;

    const enabled = Boolean(status?.enabled);
    const provider = status?.provider || 'caspos';
    const isCaspos = provider === 'caspos';
    const shiftOpen = Boolean(status?.shift_open);
    const tapxOn = Boolean(status?.pos_terminal) && isCaspos; // TapXPhone yalnız Caspos
    const Btn = ({ k, label, onClick, disabled, cls = '' }) => (
        <button type="button" disabled={disabled || !!busy || !enabled} onClick={onClick}
            className={`rounded-xl border px-4 py-3 text-sm font-bold transition-all disabled:cursor-not-allowed disabled:opacity-50 ${cls || 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}>
            {busy === k ? '...' : label}
        </button>
    );

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4"
             onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl max-h-[92vh] overflow-y-auto">
                <div className="border-b border-gray-100 p-5 flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-bold text-gray-800">💳 Fiskal Kassa</h3>
                        <p className="mt-1 text-sm text-gray-400">Shift, hesabat, çek surəti və terminal əməliyyatları</p>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${isCaspos ? 'bg-emerald-100 text-emerald-700' : 'bg-sky-100 text-sky-700'}`}>
                        {isCaspos ? 'Caspos' : 'SmartOne'}
                    </span>
                </div>

                <div className="space-y-4 p-5">
                    {loading ? (
                        <div className="py-10 text-center text-gray-400">Yüklənir...</div>
                    ) : !enabled ? (
                        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                            Bu tenant üçün kassa inteqrasiyası aktiv deyil.
                        </div>
                    ) : (
                        <>
                            {/* Növbə statusu + Yenilə */}
                            <div className="flex items-center justify-between rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
                                <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                                    <span className={`inline-block h-2.5 w-2.5 rounded-full ${shiftOpen ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                                    Növbə: {shiftOpen ? 'açıq' : 'bağlı'}
                                </span>
                                <button onClick={() => { setLoading(true); loadStatus().finally(() => setLoading(false)); }}
                                    className="text-xs font-bold text-primary-600 hover:underline">Yenilə</button>
                            </div>

                            {/* Cihazı yoxla */}
                            <button type="button" disabled={!!busy} onClick={() => run('get-info', 'Cihaz məlumatı alındı.', {}, 'Cihaz məlumatı')}
                                className="w-full rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm font-bold text-sky-700 hover:bg-sky-100 disabled:opacity-50">
                                {busy === 'get-info' ? '...' : '🔌 Cihazı yoxla (Test)'}
                            </button>

                            {/* Son əməliyyatın cihaz cavabı */}
                            {lastResult && (lastResult.data || lastResult.raw) && (
                                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs">
                                    <div className="mb-1 flex items-center justify-between">
                                        <span className="font-bold text-slate-600">{lastResult.label || 'Cihaz cavabı'}</span>
                                        <button onClick={() => setLastResult(null)} className="text-slate-400 hover:text-slate-600">✕</button>
                                    </div>
                                    {lastResult.data && typeof lastResult.data === 'object' ? (
                                        <div className="space-y-0.5">
                                            {Object.entries(lastResult.data).slice(0, 12).map(([k, v]) => (
                                                <div key={k} className="flex justify-between gap-2">
                                                    <span className="text-slate-400">{k}</span>
                                                    <span className="font-mono text-slate-700 text-right break-all">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <pre className="whitespace-pre-wrap break-all font-mono text-[11px] text-slate-600">{String(lastResult.raw || '').slice(0, 600)}</pre>
                                    )}
                                </div>
                            )}

                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <Btn k="open-shift" label="Shift aç" disabled={shiftOpen}
                                    cls="border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                    onClick={() => run('open-shift', 'Kassa növbəsi açıldı.')} />
                                <Btn k="close-shift" label="Shift bağla" disabled={!shiftOpen}
                                    cls="border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                                    onClick={() => run('close-shift', 'Kassa növbəsi bağlandı.')} />
                                <Btn k="last-receipt-copy" label="Son qaiməni çap et"
                                    onClick={() => run('last-receipt-copy', 'Sonuncu qaimə göndərildi.')} />
                                <Btn k="x-report" label="X hesabat"
                                    onClick={() => run('x-report', 'X hesabatı göndərildi.')} />
                                <div className="sm:col-span-2">
                                    <Btn k="z-report" label="Z hesabat" disabled={!shiftOpen} cls="w-full border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                                        onClick={() => run('z-report', 'Z hesabatı göndərildi.')} />
                                </div>
                            </div>

                            <p className="text-center text-[11px] text-gray-400 leading-snug">
                                Mədaxil / inkassasiya avtomatikdir — kassa hərəkətləri (borc ödənişi, xərc, ofisə köçürmə və s.) fiskal cihaza özü ötürülür.
                            </p>

                            {/* Dövri hesabat — yalnız Caspos */}
                            {isCaspos && (
                                <div className="border-t border-gray-100 pt-4">
                                    <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-2">Dövri hesabat</div>
                                    <div className="grid grid-cols-2 gap-3 mb-2">
                                        <input type="date" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)}
                                            className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-primary-400 focus:outline-none" />
                                        <input type="date" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)}
                                            className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-primary-400 focus:outline-none" />
                                    </div>
                                    <Btn k="periodic-report" label="Hesabat al" cls="w-full border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                                        onClick={() => run('periodic-report', 'Dövri hesabat göndərildi.', { dateStart: periodFrom, dateEnd: periodTo })} />
                                </div>
                            )}

                            {/* Çek surəti (nömrəyə görə) */}
                            <div className="border-t border-gray-100 pt-4">
                                <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-2">Çek surəti (nömrəyə görə)</div>
                                <div className="flex gap-2">
                                    <input type="text" value={checkNum} onChange={(e) => setCheckNum(e.target.value)}
                                        placeholder="Sənəd / satış nömrəsi"
                                        className="flex-1 rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-primary-400 focus:outline-none" />
                                    <Btn k="check-copy" label="Surəti çap et"
                                        onClick={() => run('check-copy', 'Çek surəti göndərildi.', { number: checkNum })} />
                                </div>
                                {isCaspos && <p className="mt-1 text-[11px] text-gray-400">Caspos-da nömrəyə görə surət yoxdur — son qaimə çap olunur.</p>}
                            </div>

                            {/* TapXPhone (bank terminalı) testi — yalnız Caspos + pos_terminal */}
                            {tapxOn && (
                                <div className="border-t border-gray-100 pt-4">
                                    <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-2">TapXPhone (bank terminalı) testi</div>
                                    <button type="button" disabled={!!busy} onClick={() => run('tapx-test', 'TapXPhone testi uğurlu.')}
                                        className="w-full rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm font-bold text-indigo-700 hover:bg-indigo-100 disabled:opacity-50">
                                        {busy === 'tapx-test' ? '...' : '🔌 TapXPhone yoxla'}
                                    </button>
                                    <p className="mt-1 text-[11px] text-gray-400 leading-snug">
                                        Satış etmədən yalnız bank terminalı tətbiqinin (TapXPhone) açılıb-açılmadığını yoxlayır — 0.01 ₼ test məbləği ilə.
                                    </p>
                                </div>
                            )}

                            {/* Düzəliş — yalnız Caspos */}
                            {isCaspos && (
                                <div className="border-t border-gray-100 pt-4">
                                    <button type="button" disabled={!!busy} onClick={() => run('correction', 'Düzəliş göndərildi.')}
                                        className="w-full rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-bold text-amber-700 hover:bg-amber-100 disabled:opacity-50">
                                        {busy === 'correction' ? '...' : '🧾 Düzəliş (oflayn satışlar · yalnız Caspos)'}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="flex justify-end border-t border-gray-100 p-5">
                    <button type="button" onClick={onClose}
                        className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-500 hover:bg-gray-50">
                        Bağla
                    </button>
                </div>
            </div>
        </div>
    );
}
